import { motion } from "framer-motion";
import { DollarSign, CheckCircle2, Truck, Package, Car, Home, Wrench, Users } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

const PricingPage = () => {
  const services = [
    {
      icon: Truck,
      title: "Cargo Delivery",
      color: "bg-lime-400 text-emerald-950",
      tiers: [
        { name: "Small", price: "$85-115", desc: "Fits in car trunk" },
        { name: "Medium", price: "$135-165", desc: "Requires SUV/van" },
        { name: "Large", price: "$185-215", desc: "Requires truck" }
      ],
      extras: ["Helpers: +$30-40 each", "Stairs surcharge: +$20-30"]
    },
    {
      icon: Package,
      title: "Courier",
      color: "bg-sky-500 text-white",
      tiers: [
        { name: "Envelope", price: "$12-18", desc: "Documents" },
        { name: "Small", price: "$18-28", desc: "Up to 5 lbs" },
        { name: "Medium", price: "$28-45", desc: "5-20 lbs" },
        { name: "Large", price: "$45-75", desc: "20-50 lbs" }
      ],
      extras: ["Express (1-2hr): 1.5x", "Priority (<1hr): 2x"]
    },
    {
      icon: Car,
      title: "Towing",
      color: "bg-orange-500 text-white",
      tiers: [
        { name: "Motorcycle", price: "$75-100", desc: "" },
        { name: "Sedan", price: "$95-140", desc: "" },
        { name: "SUV", price: "$120-180", desc: "" },
        { name: "Truck", price: "$150-220", desc: "" }
      ],
      extras: ["Non-drivable: +$30-50", "24/7 service available"]
    },
    {
      icon: Home,
      title: "Movers",
      color: "bg-purple-500 text-white",
      tiers: [
        { name: "Studio", price: "$250-350", desc: "1 room" },
        { name: "1 Bed", price: "$350-500", desc: "2-3 rooms" },
        { name: "2 Bed", price: "$500-750", desc: "4-5 rooms" },
        { name: "3+ Bed", price: "$750-1600", desc: "6+ rooms" }
      ],
      extras: ["Packing services: +$150-250", "Stairs: +$50-75 each"]
    },
    {
      icon: Wrench,
      title: "Roadside Assistance",
      color: "bg-red-500 text-white",
      tiers: [
        { name: "Battery Boost", price: "$50", desc: "Jump-start" },
        { name: "Flat Tire Change", price: "$60", desc: "Spare installed" },
        { name: "Fuel Delivery", price: "$55", desc: "Emergency fuel" },
        { name: "Lockout Service", price: "$65", desc: "Vehicle unlock" },
        { name: "Winching", price: "$100", desc: "Snow/mud/ditch" }
      ],
      extras: ["GPS location sharing", "24/7 availability"]
    },
    {
      icon: Users,
      title: "Laborer",
      color: "bg-rose-500 text-white",
      tiers: [
        { name: "Per Laborer", price: "$25-35/hr", desc: "Hourly rate" }
      ],
      extras: ["Loading & unloading", "Packing & cleaning", "Furniture assembly", "General labor"]
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="pricing-page">
      <Navbar />

      {/* Hero */}
      <section className="hero-bg pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="section-container relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <div className="w-16 h-16 bg-lime-400 rounded-2xl flex items-center justify-center mb-6">
              <DollarSign className="w-8 h-8 text-emerald-950" />
            </div>
            <h1 className="heading-2 text-white mb-4">Transparent Pricing</h1>
            <p className="body-lg text-stone-300">
              No hidden fees. Pay only for what you need. All prices in CAD.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="section-container py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="feature-card"
            >
              <div className={`w-12 h-12 ${service.color} rounded-xl flex items-center justify-center mb-4`}>
                <service.icon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-emerald-950 mb-4">{service.title}</h3>
              
              <div className="space-y-3 mb-6">
                {service.tiers.map((tier) => (
                  <div key={tier.name} className="flex justify-between items-center py-2 border-b border-stone-100">
                    <div>
                      <span className="font-medium text-emerald-950">{tier.name}</span>
                      {tier.desc && <span className="text-sm text-stone-500 ml-2">({tier.desc})</span>}
                    </div>
                    <span className="font-bold text-emerald-600">{tier.price}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                {service.extras.map((extra, i) => (
                  <p key={i} className="text-sm text-stone-500 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    {extra}
                  </p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* B2B Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12 p-8 bg-emerald-950 rounded-3xl text-white text-center"
        >
          <h3 className="text-2xl font-semibold mb-4">Business Pricing</h3>
          <p className="text-stone-300 max-w-2xl mx-auto">
            Volume discounts and custom pricing available for businesses. Contact our sales team for a personalized quote tailored to your delivery needs.
          </p>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
